import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Center,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceDataTable, {
  FinanceDataTableColumn,
} from "./common/FinanceDataTable";
import { financeApi } from "./financeApi";
import { formatDateTime, formatMoney } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAdminAccess } from "../Analytics/shared/AdminAccess/AdminAccessContext";

type SalesAllocationRow = {
  _id?: string;
  sourceType?: string;
  sourceId?: string;
  orderId?: string;
  orderItemId?: string;
  productTitle?: string;

  sellerUserId?: string;
  sellerId?: string;
  ownerUserId?: string;
  ownerId?: string;
  accountUserId?: string;
  beneficiaryUserId?: string;
  recipientUserId?: string;
  createdBy?: string;
  userId?: string;

  paymentStatus?: string;
  settlementStatus?: string;

  grossAmount?: number;
  platformFeeAmount?: number;
  gatewayFeeAmount?: number;
  netSellerAmount?: number;
  netAmount?: number;
  amount?: number;
  currency?: string;

  createdAt?: string;

  metadata?: Record<string, any>;
};

type SalesSummary = {
  count: number;
  grossAmount: number;
  platformFees: number;
  netPayable: number;
};

const clean = (value: unknown) => String(value || "").trim();

const normalizeRole = (value: unknown) =>
  clean(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const toRows = (payload: any): SalesAllocationRow[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;

  return [];
};

const getPaymentStatusScheme = (status?: string) => {
  const normalized = clean(status).toLowerCase();

  if (
    normalized === "paid" ||
    normalized === "success" ||
    normalized === "completed"
  ) {
    return "green";
  }

  if (
    normalized === "failed" ||
    normalized === "cancelled" ||
    normalized === "refunded"
  ) {
    return "red";
  }

  if (normalized === "pending" || normalized === "processing") {
    return "orange";
  }

  return "gray";
};

const getSettlementStatusScheme = (status?: string) => {
  const normalized = clean(status).toLowerCase();

  if (normalized === "paid_out" || normalized === "paid") {
    return "green";
  }

  if (normalized === "eligible") {
    return "blue";
  }

  if (
    normalized === "cancelled" ||
    normalized === "reversed" ||
    normalized === "failed"
  ) {
    return "red";
  }

  if (normalized === "pending" || normalized === "processing") {
    return "orange";
  }

  return "gray";
};

const getAllocationOwnerIds = (row: SalesAllocationRow) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  const addMany = (values: unknown) => {
    if (!Array.isArray(values)) return;
    values.forEach(add);
  };

  add(row.sellerUserId);
  add(row.sellerId);
  add(row.ownerUserId);
  add(row.ownerId);
  add(row.accountUserId);
  add(row.beneficiaryUserId);
  add(row.recipientUserId);
  add(row.createdBy);

  const metadata = row.metadata || {};

  add(metadata.sellerUserId);
  add(metadata.sellerId);
  add(metadata.ownerUserId);
  add(metadata.ownerId);
  add(metadata.accountUserId);
  add(metadata.beneficiaryUserId);
  add(metadata.recipientUserId);
  add(metadata.createdBy);

  addMany(metadata.sellerUserIds);
  addMany(metadata.ownerUserIds);
  addMany(metadata.accountUserIds);

  /*
   * Legacy fallback: only use the generic userId when no stronger ownership
   * field exists on this allocation.
   */
  if (ids.size === 0) {
    add(row.userId);
    add(metadata.userId);
  }

  return ids;
};

const isAllocationOwnedByAccount = (row: SalesAllocationRow, userId: string) =>
  getAllocationOwnerIds(row).has(clean(userId));

const buildSummary = (rows: SalesAllocationRow[]): SalesSummary =>
  rows.reduce<SalesSummary>(
    (current, row) => {
      const grossAmount = Number(row.grossAmount ?? row.amount ?? 0);

      const platformFeeAmount = Number(row.platformFeeAmount || 0);

      const gatewayFeeAmount = Number(row.gatewayFeeAmount || 0);

      const netPayable = Number(
        row.netSellerAmount ??
          row.netAmount ??
          grossAmount - platformFeeAmount - gatewayFeeAmount,
      );

      current.count += 1;
      current.grossAmount += grossAmount;
      current.platformFees += platformFeeAmount + gatewayFeeAmount;
      current.netPayable += netPayable;

      return current;
    },
    {
      count: 0,
      grossAmount: 0,
      platformFees: 0,
      netPayable: 0,
    },
  );

const AdminSalesAllocationsPage: React.FC = () => {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const {
    userId,
    role,
    isSuperAdmin,
    loading: accessLoading,
  } = useAdminAccess();

  const normalizedRole = normalizeRole(role);
  const hasFinanceAdminAccess = isSuperAdmin || normalizedRole === "admin";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SalesAllocationRow[]>([]);
  const [summary, setSummary] = useState<SalesSummary>({
    count: 0,
    grossAmount: 0,
    platformFees: 0,
    netPayable: 0,
  });
  const [query, setQuery] = useState("");

  const load = useCallback(
    async (q = "") => {
      if (accessLoading) return;

      if (!userId || !hasFinanceAdminAccess) {
        setRows([]);
        setSummary(buildSummary([]));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const params: Record<string, string | number> = {
          q: clean(q),
          limit: 500,
        };

        /*
         * These are API query hints only. The returned rows are always filtered
         * again below so the table does not rely on the API honoring them.
         */
        if (!isSuperAdmin) {
          params.sellerUserId = userId;
          params.ownerUserId = userId;
          params.accountUserId = userId;
        }

        const response = await financeApi.getAdminSales(params);
        const returnedRows = toRows(response);

        const visibleRows = isSuperAdmin
          ? returnedRows
          : returnedRows.filter((row) =>
              isAllocationOwnedByAccount(row, userId),
            );

        setRows(visibleRows);
        setSummary(buildSummary(visibleRows));
      } catch (error: any) {
        console.error("Failed to load sales allocations:", error);

        toast({
          status: "error",
          title: "Failed to load sales allocations",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Please try again.",
          duration: 4000,
          isClosable: true,
        });

        setRows([]);
        setSummary(buildSummary([]));
      } finally {
        setLoading(false);
      }
    },
    [accessLoading, hasFinanceAdminAccess, isSuperAdmin, toast, userId],
  );

  useEffect(() => {
    if (accessLoading) return;
    void load("");
  }, [accessLoading, load]);

  const columns = useMemo<Array<FinanceDataTableColumn<SalesAllocationRow>>>(
    () => [
      {
        key: "type",
        header: "Type",
        render: (row) => row.sourceType || "—",
      },
      {
        key: "seller",
        header: "Seller",
        render: (row) => (
          <>
            <Text color={theme.text} fontWeight="700">
              {isSuperAdmin
                ? row.sellerUserId ||
                  row.ownerUserId ||
                  row.accountUserId ||
                  "—"
                : "Current account"}
            </Text>

            {!isSuperAdmin && (
              <Text fontSize="xs" color={theme.textMuted}>
                {userId}
              </Text>
            )}
          </>
        ),
      },
      {
        key: "reference",
        header: "Reference",
        render: (row) => (
          <>
            <Text color={theme.text}>{row.orderId || row.sourceId || "—"}</Text>

            {!!row.orderItemId && (
              <Text fontSize="xs" color={theme.textMuted}>
                {row.orderItemId}
              </Text>
            )}
          </>
        ),
      },
      {
        key: "title",
        header: "Title",
        render: (row) => row.productTitle || "—",
      },
      {
        key: "paymentStatus",
        header: "Payment",
        render: (row) => (
          <Badge colorScheme={getPaymentStatusScheme(row.paymentStatus)}>
            {row.paymentStatus || "pending"}
          </Badge>
        ),
      },
      {
        key: "settlementStatus",
        header: "Settlement",
        render: (row) => (
          <Badge colorScheme={getSettlementStatusScheme(row.settlementStatus)}>
            {row.settlementStatus || "pending"}
          </Badge>
        ),
      },
      {
        key: "gross",
        header: "Gross",
        isNumeric: true,
        render: (row) =>
          formatMoney(row.grossAmount ?? row.amount, row.currency),
      },
      {
        key: "fees",
        header: "Fees",
        isNumeric: true,
        render: (row) =>
          formatMoney(
            Number(row.platformFeeAmount || 0) +
              Number(row.gatewayFeeAmount || 0),
            row.currency,
          ),
      },
      {
        key: "net",
        header: "Net",
        isNumeric: true,
        render: (row) =>
          formatMoney(row.netSellerAmount ?? row.netAmount, row.currency),
      },
      {
        key: "date",
        header: "Date",
        render: (row) => formatDateTime(row.createdAt),
      },
    ],
    [isSuperAdmin, theme.text, theme.textMuted, userId],
  );

  if (accessLoading || loading) {
    return (
      <FinancePageShell title="Sales Allocations">
        <VStack py={10}>
          <Spinner />
          <Text color={theme.textMuted}>Loading sales allocations...</Text>
        </VStack>
      </FinancePageShell>
    );
  }

  if (!userId) {
    return (
      <FinancePageShell title="Sales Allocations">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Sign in before opening sales allocations.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  if (!hasFinanceAdminAccess) {
    return (
      <FinancePageShell title="Sales Allocations">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Only admin and superadmin accounts can open sales allocations.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  return (
    <FinancePageShell
      title="Sales Allocations"
      subtitle={
        isSuperAdmin
          ? "Everything sold across all accounts, the fees taken, and each seller payout state."
          : "Everything sold by your account, the fees taken, and your payout state."
      }
    >
      <HStack mb={4} spacing={2} flexWrap="wrap">
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          colorScheme={isSuperAdmin ? "purple" : "blue"}
        >
          {isSuperAdmin ? "All platform accounts" : "Current account only"}
        </Badge>

        <Text fontSize="xs" color={theme.textMuted}>
          Role: {role || "user"} · UID: {userId}
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <FinanceStatCard label="Rows" value={String(summary.count)} />

        <FinanceStatCard
          label="Gross"
          value={formatMoney(summary.grossAmount)}
        />

        <FinanceStatCard
          label="Platform Fees"
          value={formatMoney(summary.platformFees)}
        />

        <FinanceStatCard
          label="Net Payable"
          value={formatMoney(summary.netPayable)}
        />
      </SimpleGrid>

      <FinanceSectionCard
        title={isSuperAdmin ? "All Allocations" : "Your Allocations"}
        rightSlot={
          <HStack w={{ base: "100%", md: "auto" }} flexWrap="wrap">
            <Input
              placeholder="Search seller, order, product..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void load(query);
                }
              }}
              onBlur={() => void load(query)}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              _placeholder={{
                color: theme.inputPlaceholder,
              }}
              minW={{ base: "100%", md: "280px" }}
            />

            <Button
              size="sm"
              onClick={() => void load(query)}
              isLoading={loading}
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{
                bgImage: theme.gradients.primaryButtonHover,
              }}
            >
              Search
            </Button>
          </HStack>
        }
      >
        <FinanceDataTable
          rows={rows}
          columns={columns}
          getRowKey={(row, index) =>
            String(
              row._id ||
                row.orderItemId ||
                `${row.orderId || row.sourceId || "allocation"}:${index}`,
            )
          }
          emptyText={
            isSuperAdmin
              ? "No sales allocations found."
              : "No sales allocations are linked to your account."
          }
          initialPageSize={10}
        />
      </FinanceSectionCard>
    </FinancePageShell>
  );
};

export default AdminSalesAllocationsPage;
